import { Coordinates, DailyHistory, DailyHistoryRecord, ExperienceSnapshot, Settings, Stats, StatsStore } from '../types';
import { GAME_MODES } from './modes';

const STATS_KEY = 'cp_stats_v2';
const HISTORY_KEY = 'cp_daily_history_v3';
const SETTINGS_KEY = 'cp_settings_v2';
const EXPERIENCE_KEY = 'cp_experience_v1';
const LOCATION_KEY = 'cp_location_v1';
const LOCAL_WARNING_KEY = 'cp_local_warning_ack_v1';
const PIN_LOCATION_KEY = 'cp_pin_location_v1';

interface StorageBackend {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const memoryStore = new Map<string, string>();

function getBackend(): StorageBackend {
  if (typeof window !== 'undefined' && window.localStorage) {
    return window.localStorage;
  }
  return {
    getItem: (key) => memoryStore.get(key) ?? null,
    setItem: (key, value) => memoryStore.set(key, value),
  };
}

/**
 * Exposes the storage backend used across the app so new features can reuse
 * the same in-memory fallback during tests.
 */
export function getStorageBackend(): StorageBackend {
  return getBackend();
}

const defaultStats: StatsStore = GAME_MODES.reduce((acc, mode) => {
  acc[mode] = { totalGames: 0, totalCorrectGuesses: 0, bestStreak: 0 };
  return acc;
}, {} as StatsStore);

const defaultSettings: Settings = {
  theme: 'dark',
  muted: false,
};

const defaultExperience: ExperienceSnapshot = {
  totalExp: 0,
};

export function loadStats(): StatsStore {
  const backend = getBackend();
  const raw = backend.getItem(STATS_KEY);
  if (!raw) {
    return GAME_MODES.reduce((acc, mode) => {
      acc[mode] = { ...defaultStats[mode] };
      return acc;
    }, {} as StatsStore);
  }
  try {
    const parsed = JSON.parse(raw) as Partial<StatsStore>;
    return GAME_MODES.reduce((acc, mode) => {
      const existing = parsed?.[mode] as Partial<Stats> | undefined;
      acc[mode] = { ...defaultStats[mode], ...(existing ?? {}) };
      return acc;
    }, {} as StatsStore);
  } catch (error) {
    console.warn('Failed to parse stats, using defaults', error);
    return GAME_MODES.reduce((acc, mode) => {
      acc[mode] = { ...defaultStats[mode] };
      return acc;
    }, {} as StatsStore);
  }
}

export function saveStats(stats: StatsStore): void {
  const backend = getBackend();
  backend.setItem(STATS_KEY, JSON.stringify(stats));
}

export function loadHistory(): DailyHistory {
  const backend = getBackend();
  const raw = backend.getItem(HISTORY_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as Record<string, Partial<DailyHistoryRecord>>;
    return Object.fromEntries(
      Object.entries(parsed).map(([key, value]) => {
        const seed = value?.seed ?? `daily-${key}`;
        const playedAt = value?.playedAt ?? new Date().toISOString();
        return [key, { seed, playedAt, completed: value?.completed, streak: value?.streak }];
      }),
    );
  } catch (error) {
    console.warn('Failed to parse history, using empty', error);
    return {};
  }
}

export function saveHistory(history: DailyHistory): void {
  const backend = getBackend();
  backend.setItem(HISTORY_KEY, JSON.stringify(history));
}

export function loadSettings(): Settings {
  const backend = getBackend();
  const raw = backend.getItem(SETTINGS_KEY);
  if (!raw) return { ...defaultSettings };
  try {
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return { ...defaultSettings, ...parsed };
  } catch (error) {
    console.warn('Failed to parse settings, using defaults', error);
    return { ...defaultSettings };
  }
}

export function saveSettings(settings: Settings): void {
  const backend = getBackend();
  backend.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

export function loadExperience(): ExperienceSnapshot {
  const backend = getBackend();
  const raw = backend.getItem(EXPERIENCE_KEY);
  if (!raw) return { ...defaultExperience };
  try {
    const parsed = JSON.parse(raw) as Partial<ExperienceSnapshot>;
    return { ...defaultExperience, ...parsed };
  } catch (error) {
    console.warn('Failed to parse experience, using defaults', error);
    return { ...defaultExperience };
  }
}

export function saveExperience(snapshot: ExperienceSnapshot): void {
  const backend = getBackend();
  backend.setItem(EXPERIENCE_KEY, JSON.stringify(snapshot));
}

export function loadLocation(): Coordinates | null {
  const backend = getBackend();
  const raw = backend.getItem(LOCATION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<Coordinates>;
    if (typeof parsed.lat === 'number' && typeof parsed.lon === 'number') {
      return { lat: parsed.lat, lon: parsed.lon };
    }
    return null;
  } catch (error) {
    console.warn('Failed to parse saved location', error);
    return null;
  }
}

export function saveLocation(location: Coordinates): void {
  const backend = getBackend();
  backend.setItem(LOCATION_KEY, JSON.stringify(location));
}

export function clearLocation(): void {
  const backend = getBackend();
  backend.setItem(LOCATION_KEY, '');
}

export function loadPinLocation(): Coordinates | null {
  const backend = getBackend();
  const raw = backend.getItem(PIN_LOCATION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<Coordinates>;
    if (typeof parsed.lat === 'number' && typeof parsed.lon === 'number') {
      return { lat: parsed.lat, lon: parsed.lon };
    }
    return null;
  } catch (error) {
    console.warn('Failed to parse saved pin location', error);
    return null;
  }
}

export function savePinLocation(location: Coordinates): void {
  const backend = getBackend();
  backend.setItem(PIN_LOCATION_KEY, JSON.stringify(location));
}

export function clearPinLocation(): void {
  const backend = getBackend();
  backend.setItem(PIN_LOCATION_KEY, '');
}

export function loadLocalWarningAcknowledged(): boolean {
  const backend = getBackend();
  return backend.getItem(LOCAL_WARNING_KEY) === '1';
}

export function saveLocalWarningAcknowledged(): void {
  const backend = getBackend();
  backend.setItem(LOCAL_WARNING_KEY, '1');
}

export function resetAll(): { stats: StatsStore; history: DailyHistory } {
  const stats = GAME_MODES.reduce((acc, mode) => {
    acc[mode] = { ...defaultStats[mode] };
    return acc;
  }, {} as StatsStore);
  saveStats(stats);
  saveHistory({});
  return { stats, history: {} };
}
